// =============================================================================
// w13 "Qadr" — Bukhara Royal variant.
//
// Tone:
//   A maximalist royal-pavilion wedding inspired by the Sitorai Mokhi-Khosa
//   palace and Uzbek suzani embroidery. Dark mode: deep aubergine night with
//   saffron-gold suzani roundels embroidering themselves across the page.
//
// Sections:
//   Hero (photo + giant Bodoni names + radial gold glow + suzani draw-on)
//     → Countdown → Story (trimmed) → Gallery → Schedule → Map → RSVP →
//       Share → Footer
//
// Signature animation:
//   Every chrysanthemum roundel embroiders itself onto the page background:
//   a stroke-dashoffset sweep stagger-driven by the roundel's distance from
//   top-left, ~3s ease-in-out total. Reduced-motion: pre-drawn.
//
// Sounds:
//   chime → RSVP confirm
//   swoosh → suzani draw kick-off
//   ding → schedule reveal
//
// Music — a small floating ambient toggle in the bottom-right plays a deep
// Persian-flavoured Aeolian pad via the shared useAmbient hook.
// =============================================================================

import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import type { VariantProps } from "./types";
import { useSound } from "@/lib/sound";
import { useAmbient, useReducedMotion } from "./_ambient";
import { MusicToggle } from "@/components/MusicToggle";
import {
  CountdownSection,
  StorySection,
  GallerySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";

// ─── Palette helpers ─────────────────────────────────────────────────────────
const CREAM = "#F4E5C2";

// ─── Suzani motif catalog (page background) ─────────────────────────────────
// We compose a 6×8 lattice of roundels across the page background. Each
// roundel renders as paths with a stroke-dashoffset trick so it can "draw on"
// when the user scrolls past or on initial mount.
function PageSuzaniBackdrop({
  accent,
  ink,
  drawOn,
}: {
  accent: string;
  ink: string;
  drawOn: boolean;
}) {
  const cols = 6;
  const rows = 8;

  // Each roundel gets a stagger delay derived from (r+c). Total sweep ~3s.
  const cells: Array<{ r: number; c: number; delay: number }> = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const t = (r + c) / (rows + cols - 2);
      cells.push({ r, c, delay: t * 2.6 });
    }
  }

  return (
    <svg
      aria-hidden
      viewBox={`0 0 ${cols * 100} ${rows * 100}`}
      preserveAspectRatio="xMidYMid slice"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        opacity: 0.18,
        pointerEvents: "none",
      }}
    >
      {cells.map(({ r, c, delay }) => (
        <g key={`${r}-${c}`} transform={`translate(${c * 100} ${r * 100})`}>
          {/* outer ring */}
          <circle
            cx="50"
            cy="50"
            r="42"
            fill="none"
            stroke={accent}
            strokeWidth="0.9"
            strokeDasharray="280"
            strokeDashoffset={drawOn ? 0 : 280}
            style={{
              transition: drawOn ? `stroke-dashoffset 1.2s ease-in-out ${delay}s` : undefined,
            }}
          />
          <circle
            cx="50"
            cy="50"
            r="36"
            fill="none"
            stroke={ink}
            strokeWidth="0.5"
            opacity="0.7"
            strokeDasharray="240"
            strokeDashoffset={drawOn ? 0 : 240}
            style={{
              transition: drawOn ? `stroke-dashoffset 1.2s ease-in-out ${delay + 0.05}s` : undefined,
            }}
          />
          {/* 10 chrysanthemum petals */}
          {Array.from({ length: 10 }).map((_, i) => {
            const a = (i * 36 * Math.PI) / 180;
            const cx = 50 + Math.cos(a) * 22;
            const cy = 50 + Math.sin(a) * 22;
            return (
              <ellipse
                key={i}
                cx={cx}
                cy={cy}
                rx="8"
                ry="4"
                fill="none"
                stroke={accent}
                strokeWidth="0.8"
                strokeDasharray="60"
                strokeDashoffset={drawOn ? 0 : 60}
                transform={`rotate(${i * 36} ${cx} ${cy})`}
                style={{
                  transition: drawOn
                    ? `stroke-dashoffset 0.8s ease-in-out ${delay + 0.15 + i * 0.02}s`
                    : undefined,
                }}
              />
            );
          })}
          {/* eight-fold inner star */}
          {Array.from({ length: 8 }).map((_, i) => {
            const a = (i * 45 * Math.PI) / 180;
            const x2 = 50 + Math.cos(a) * 12;
            const y2 = 50 + Math.sin(a) * 12;
            return (
              <line
                key={i}
                x1="50"
                y1="50"
                x2={x2}
                y2={y2}
                stroke={accent}
                strokeWidth="0.7"
                strokeDasharray="14"
                strokeDashoffset={drawOn ? 0 : 14}
                style={{
                  transition: drawOn
                    ? `stroke-dashoffset 0.6s ease-in-out ${delay + 0.3}s`
                    : undefined,
                }}
              />
            );
          })}
          <circle cx="50" cy="50" r="4" fill={accent} opacity={drawOn ? 1 : 0} style={{ transition: `opacity 0.4s ease-in-out ${delay + 0.5}s` }} />
        </g>
      ))}
    </svg>
  );
}

// ─── Story content (trimmed to 2-3 sentences) ────────────────────────────────
const STORY_BODY =
  "Shahnoza ham, Komron ham — Buxoroning ko'hna oilalaridan. Ular Buxoro to'qimachilik muzeyida, " +
  "eski suzani ko'rgazmasining ochilishi kunida uchrashishdi va uch yil davomida sevgilari sovg'a " +
  "qilingan suzani parchalari orqali yashadi. Komron taklifni Sitorai Mohi Xosa saroyining ayvonida, " +
  "to'rt avlod tikiltirgan oq suzanini Shahnozaning yelkasiga yopgancha aytdi.";

// ─── Schedule ────────────────────────────────────────────────────────────────
const SCHEDULE: Array<[string, string]> = [
  ["16:00", "Mehmonlarni kutib olish — non, choy, anor sharbati"],
  ["17:00", "Nikoh ostida — saroy ayvonida fotiha o'qish"],
  ["18:00", "Fotiha-osh — Buxoro oshi tortilishi"],
  ["19:30", "Raqs va musiqa — kuyov-kelin birinchi raqsi"],
  ["21:00", "Boylik tortilishi — kelin-kuyov sovg'alari namoyishi"],
  ["22:30", "Mahalliy musiqa — Buxoro shashmaqomi va doirachilar"],
  ["00:00", "Mehmonlarning chiqishi — yo'lga oq sut va nondan"],
];

// ─── Reveal helper ───────────────────────────────────────────────────────────
interface RevealProps { children: React.ReactNode; reduce: boolean; delay?: number; style?: CSSProperties; }
function Reveal({ children, reduce, delay = 0, style }: RevealProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState<boolean>(reduce);
  useEffect(() => {
    if (reduce) { setShown(true); return; }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") { setShown(true); return; }
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) if (e.isIntersecting) { setShown(true); io.disconnect(); break; }
    }, { threshold: 0.18, rootMargin: "0px 0px -8% 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [reduce]);
  return (
    <div ref={ref} style={{
      ...style,
      opacity: shown ? 1 : 0,
      transform: shown ? "translateY(0)" : "translateY(18px)",
      transition: reduce ? "none" : `opacity 1s ease-out ${delay}ms, transform 1s ease-out ${delay}ms`,
    }}>{children}</div>
  );
}

// ─── Variant ─────────────────────────────────────────────────────────────────
export function BukharaRoyalVariant({ t, isDark, ctx }: VariantProps) {
  const { play, enabled: soundEnabled } = useSound();
  const reduced = useReducedMotion();

  // Suzani draw-on toggle. true => stroke-dashoffset = 0 => embroidery visible.
  const [drawn, setDrawn] = useState<boolean>(reduced);

  // Music toggle — deep Persian-flavoured Aeolian pad.
  const [music, setMusic] = useState<boolean>(false);
  useAmbient(music && soundEnabled, {
    freqs: [110.0, 130.81, 164.81, 220.0],
    type: "triangle",
    master: 0.03,
    lfo: 0.1,
  });

  const toggleMusic = useCallback(() => {
    setMusic((p) => {
      const next = !p;
      play(next ? "music-on" : "music-off");
      return next;
    });
  }, [play]);

  // Trigger the suzani draw on mount + play swoosh once.
  useEffect(() => {
    if (reduced) { setDrawn(true); return; }
    const id = window.setTimeout(() => {
      play("swoosh");
      setDrawn(true);
    }, 250);
    return () => window.clearTimeout(id);
  }, [reduced, play]);

  // Schedule reveal — soft bell when the schedule scrolls into view.
  const scheduleRef = useRef<HTMLDivElement | null>(null);
  const [scheduleRevealed, setScheduleRevealed] = useState(false);
  useEffect(() => {
    if (!scheduleRef.current || scheduleRevealed) return;
    const el = scheduleRef.current;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setScheduleRevealed(true);
            play("ding");
            obs.disconnect();
          }
        });
      },
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [scheduleRevealed, play]);

  // RSVP wrapper that plays gong-like chime then forwards
  const wrappedSetRsvp = (v: string | null) => {
    if (v) play("chime");
    ctx.rsvp.setRsvp(v);
  };

  const heroPhoto = t.photos[0];

  return (
    <div className="v-w13">
      <style>{`
        @keyframes w13-tassel-sway {
          0%, 100% { transform: translateY(-50%) rotate(-2deg); }
          50% { transform: translateY(-50%) rotate(2deg); }
        }
        @keyframes w13-glow {
          0%, 100% { opacity: 0.55; transform: translate(-50%,-50%) scale(1); }
          50% { opacity: 0.8; transform: translate(-50%,-50%) scale(1.05); }
        }
        @media (prefers-reduced-motion: reduce) {
          .w13-anim { animation: none !important; }
        }
        @media (max-width: 640px) {
          .w13-suzani-bg { display: none; }
        }
        .w13-music-btn {
          transition: transform .2s, box-shadow .25s;
        }
        .w13-music-btn:hover { transform: scale(1.08); box-shadow: 0 12px 32px ${t.accent}66; }

        .w13-sched-wrap [class*="row"], .w13-sched-wrap li, .w13-sched-wrap tr {
          transition: background .25s ease-out, transform .25s ease-out;
        }
        .w13-sched-wrap [class*="row"]:hover, .w13-sched-wrap li:hover, .w13-sched-wrap tr:hover {
          background: ${t.accent}14;
          transform: translateX(4px);
        }
        .w13-rsvp-wrap button {
          transition: transform .2s ease-out, box-shadow .25s ease-out, background .25s ease-out;
        }
        .w13-rsvp-wrap button:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 24px ${t.accent}40;
        }
        @media (max-width: 768px) {
          .v-w13 .v-w13-hero { padding: 64px 18px 48px !important; min-height: auto !important; }
          .v-w13 .v-w13-hero h1 { font-size: clamp(36px, 12vw, 72px) !important; }
          .v-w13 .w13-music-btn { min-height: 44px; min-width: 44px; }
          /* Date/venue line: shrink letter-spacing and allow wrapping */
          .v-w13 .w13-date-venue {
            font-size: 11px !important;
            letter-spacing: 0.22em !important;
            white-space: normal !important;
            word-break: break-word !important;
            max-width: 320px !important;
            margin: 0 auto !important;
          }
          /* Horizontal rule: cap to viewport */
          .v-w13 .w13-hr { width: min(260px, 70vw) !important; }
        }
        .v-w13 button:focus-visible { outline: 2px solid ${t.accent}; outline-offset: 2px; }
      `}</style>

      {/* ═══ HERO ═══════════════════════════════════════════════════════════ */}
      <section
        className="hero-fill v-w13-hero"
        style={{
          minHeight: "100vh",
          padding: "100px 24px 80px",
          background: t.bg,
          color: t.ink,
          display: "grid",
          placeItems: "center",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Full-bleed hero photo behind everything */}
        {heroPhoto && (
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: `url("${heroPhoto}")`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "brightness(0.55) saturate(0.9)",
              opacity: 0.85,
            }}
          />
        )}
        {/* Aubergine wash on top of photo */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(180deg, ${t.bg}cc 0%, ${t.bg}aa 50%, ${t.bg}ee 100%)`,
          }}
        />
        {/* Soft suzani tile overlay */}
        <div
          aria-hidden
          className="w13-suzani-bg"
          style={{ position: "absolute", inset: 0, opacity: 0.65 }}
        >
          <PageSuzaniBackdrop accent={t.accent} ink={CREAM} drawOn={drawn} />
        </div>
        {/* Radial gold glow behind the names */}
        <div
          aria-hidden
          className="w13-anim"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: "min(700px, 90vw)",
            height: "min(700px, 90vw)",
            background: `radial-gradient(circle at center, ${t.accent}55 0%, ${t.accent}22 30%, transparent 65%)`,
            transform: "translate(-50%,-50%)",
            animation: reduced ? undefined : "w13-glow 6s ease-in-out infinite",
            filter: "blur(30px)",
            pointerEvents: "none",
          }}
        />

        <div style={{ position: "relative", zIndex: 1, maxWidth: 900 }}>
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.5em",
              color: t.accent,
              marginBottom: 28,
            }}
          >
            QADRDON · BUXORO · 2026
          </div>
          <h1
            style={{
              fontFamily: t.font,
              fontStyle: "italic",
              fontSize: "clamp(56px, 11vw, 140px)",
              lineHeight: 0.95,
              fontWeight: 400,
              color: t.accent,
              textShadow: `0 4px 30px ${t.bg}, 0 0 60px ${t.accent}55`,
              margin: 0,
            }}
          >
            {t.names}
          </h1>
          <div
            aria-hidden
            className="w13-hr"
            style={{
              width: "min(360px, 70vw)",
              height: 1,
              background: `linear-gradient(90deg, transparent, ${t.accent}, transparent)`,
              margin: "32px auto",
            }}
          />
          <div
            className="w13-date-venue"
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: "0.4em",
              color: CREAM,
            }}
          >
            {t.date} · {t.venue.toUpperCase()}
          </div>
        </div>
      </section>

      {/* ═══ COUNTDOWN ══════════════════════════════════════════════════════ */}
      <Reveal reduce={reduced}>
        <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />
      </Reveal>

      {/* ═══ STORY ══════════════════════════════════════════════════════════ */}
      <Reveal reduce={reduced}>
        <StorySection
          t={t}
          eyebrow="Bizning hikoyamiz"
          title="Suzani ostida tug'ilgan sevgi"
          body={STORY_BODY}
        />
      </Reveal>

      {/* ═══ GALLERY ════════════════════════════════════════════════════════ */}
      <Reveal reduce={reduced}>
        <GallerySection t={t} />
      </Reveal>

      {/* ═══ SCHEDULE ═══════════════════════════════════════════════════════ */}
      <Reveal reduce={reduced}>
        <div ref={scheduleRef} className="w13-sched-wrap">
          <ScheduleSection t={t} items={SCHEDULE} />
        </div>
      </Reveal>

      {/* ═══ MAP ════════════════════════════════════════════════════════════ */}
      <Reveal reduce={reduced}>
        <MapSection
          t={t}
          isDark={isDark}
          addressLine="Sitorai Mohi Xosa, Buxoro shahri, O'zbekiston"
        />
      </Reveal>

      {/* ═══ RSVP ═══════════════════════════════════════════════════════════ */}
      <Reveal reduce={reduced}>
        <div className="w13-rsvp-wrap">
          <RsvpSection
            t={t}
            isDark={isDark}
            rsvp={ctx.rsvp.rsvp}
            setRsvp={wrappedSetRsvp}
            guests={ctx.rsvp.guests}
            setGuests={ctx.rsvp.setGuests}
            guestName={ctx.rsvp.guestName}
            setGuestName={ctx.rsvp.setGuestName}
            label="Qadrdonmiz, biz bilan bo'ling"
          />
        </div>
      </Reveal>

      {/* ═══ SHARE + FOOTER ════════════════════════════════════════════════ */}
      <ShareSection t={t} />
      <GuestFooter t={t} />

      {/* ═══ Floating music toggle ═════════════════════════════════════════ */}
      <MusicToggle
        on={music}
        onToggle={toggleMusic}
        accent={t.accent}
        ink={t.ink}
        style={{ bottom: 20, right: 20 }}
        className="w13-music-btn"
      />
    </div>
  );
}
